import { useState } from 'react'
import Modal from '../components/Modal'
import { ErrorText } from '../components/Form'
import { CrewFields, InviteResult, initialCrewDraft } from '../forms/CrewFields'
import { ConfirmDelete } from './TravelDialogs'
import { addCrewMember, updateCrewMember, removeCrewMember } from '../lib/mutations'
import type { CrewDraft } from '../lib/mutations'
import type { Tour, TourCrew, WithId } from '../types/models'

export default function CrewDialog({
  open,
  member,
  tour,
  inviter,
  onClose,
}: {
  open: boolean
  member?: WithId<TourCrew> | null
  tour: WithId<Tour>
  inviter: { id: string; name: string }
  onClose: () => void
}) {
  const [draft, setDraft] = useState<CrewDraft>(() => initialCrewDraft(member))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<{ joiningCode: string | null; existing: boolean } | null>(
    null,
  )

  const patch = (next: Partial<CrewDraft>) => setDraft((previous) => ({ ...previous, ...next }))
  const valid = draft.name.trim().length > 0

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      if (member) {
        await updateCrewMember(member.id, draft)
        onClose()
      } else {
        const outcome = await addCrewMember(draft, tour, inviter)
        // Don't dismiss on an invite — the joining code is the whole point and
        // closing the sheet would throw it away.
        setResult({ joiningCode: outcome.joiningCode, existing: !!outcome.existingUserId })
      }
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (result) {
    return (
      <Modal
        open={open}
        title="Invite sent"
        onClose={onClose}
        footer={
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <InviteResult
          name={draft.name}
          email={draft.email}
          tourName={tour.tourName}
          joiningCode={result.joiningCode}
          existing={result.existing}
        />
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      title={member ? 'Edit crew member' : 'Add crew'}
      onClose={onClose}
      footer={
        <>
          {member && (
            <button
              className="btn btn-danger"
              style={{ marginRight: 'auto' }}
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              Remove
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : member ? 'Save' : 'Add & invite'}
          </button>
        </>
      }
    >
      <div className="stack">
        <CrewFields draft={draft} patch={patch} isNew={!member} />

        <ErrorText>{error}</ErrorText>

        {confirming && member && (
          <ConfirmDelete
            label={member.name}
            busy={busy}
            onCancel={() => setConfirming(false)}
            onConfirm={async () => {
              setBusy(true)
              try {
                await removeCrewMember(member)
                onClose()
              } catch (caught) {
                setError((caught as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          />
        )}
      </div>
    </Modal>
  )
}
